import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import Chart from "../charts/chart";
import useChart from "../charts/use-chart";
import { formatAsCLP } from "src/utils/formatNumber";
import {
  formatearPorcentaje,
  limitarPorcentajeGrafico,
  MontoResumen,
} from "./kpi-utils";

AppKpiEficienciaCobranza.propTypes = {
  data: PropTypes.shape({
    actual: PropTypes.object,
    anterior: PropTypes.object,
    periodoActual: PropTypes.object,
    periodoAnterior: PropTypes.object,
    porSucursal: PropTypes.array,
    variacionPuntosPorcentuales: PropTypes.number,
  }),
  error: PropTypes.bool,
  loading: PropTypes.bool,
};

export default function AppKpiEficienciaCobranza({
  data,
  error = false,
  loading = false,
}) {
  const theme = useTheme();
  const actual = data?.actual || {};
  const eficiencia = actual.eficienciaPorcentaje;
  const variacion = data?.variacionPuntosPorcentuales;
  const porSucursal = data?.porSucursal || [];

  const chartOptions = useChart({
    chart: {
      sparkline: {
        enabled: true,
      },
    },
    colors: [theme.palette.primary.main],
    labels: ["Eficiencia"],
    legend: {
      show: false,
    },
    plotOptions: {
      radialBar: {
        hollow: {
          size: "66%",
        },
        track: {
          margin: 0,
        },
        dataLabels: {
          name: {
            offsetY: 24,
          },
          value: {
            offsetY: -16,
            fontSize: "28px",
            fontWeight: 700,
            formatter: () => formatearPorcentaje(eficiencia),
          },
        },
      },
    },
    tooltip: {
      enabled: false,
    },
  });

  const periodoActual = data?.periodoActual
    ? `${data.periodoActual.inicio} a ${data.periodoActual.fin}`
    : "";
  const periodoAnterior = data?.periodoAnterior
    ? `${data.periodoAnterior.inicio} a ${data.periodoAnterior.fin}`
    : "";

  if (loading) {
    return (
      <Card sx={{ minHeight: 360 }}>
        <CardHeader
          title={<Skeleton width={220} />}
          subheader={<Skeleton width={320} />}
        />
        <Grid container spacing={3} sx={{ p: 3 }}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="circular" width={210} height={210} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              {[1, 2, 3].map((item) => (
                <Skeleton key={item} height={48} />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton height={180} />
          </Grid>
        </Grid>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ minHeight: 360 }}>
        <CardHeader
          title="Eficiencia de cobranza"
          subheader="Recaudado / facturación cobrable del período (excluye incobrables)"
        />
        <Box sx={{ px: 3, pb: 3 }}>
          <Alert severity="error">
            No fue posible obtener el indicador. Intenta nuevamente.
          </Alert>
        </Box>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card sx={{ minHeight: 360 }}>
        <CardHeader
          title="Eficiencia de cobranza"
          subheader="Recaudado / facturación cobrable del período (excluye incobrables)"
        />
        <Box sx={{ px: 3, pb: 3 }}>
          <Alert severity="info">
            No hay información disponible para los filtros seleccionados.
          </Alert>
        </Box>
      </Card>
    );
  }

  const colorVariacion =
    variacion > 0
      ? "success.main"
      : variacion < 0
        ? "error.main"
        : "text.secondary";

  return (
    <Card
      sx={{
        minHeight: 360,
        borderTop: `4px solid ${theme.palette.primary.main}`,
      }}
    >
      <CardHeader
        title="Eficiencia de cobranza"
        subheader={`Recaudado por fecha de pago / facturación cobrable (excluye incobrables) · ${periodoActual}`}
        sx={{ pb: 1 }}
      />

      <Grid container spacing={4} alignItems="center" sx={{ px: 3, pb: 3 }}>
        <Grid item xs={12} md={4}>
          <Stack alignItems="center">
            <Chart
              type="radialBar"
              series={[limitarPorcentajeGrafico(eficiencia)]}
              options={chartOptions}
              width={220}
              height={220}
            />

            <Typography
              variant="body2"
              sx={{ color: colorVariacion, textAlign: "center" }}
            >
              {variacion === null || variacion === undefined
                ? "Sin base comparable en el período anterior"
                : `${variacion > 0 ? "+" : ""}${variacion.toLocaleString(
                    "es-CL",
                  )} pp respecto de ${periodoAnterior}`}
            </Typography>

            {eficiencia > 100 && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: 1, textAlign: "center" }}
              >
                Puede superar 100% cuando se cobran facturas de períodos
                anteriores.
              </Typography>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Stack
            divider={<Divider flexItem sx={{ borderStyle: "dashed" }} />}
            spacing={2.5}
          >
            <MontoResumen
              label="Facturado en el período"
              value={actual.facturado}
            />
            <MontoResumen
              label="Recaudado por fecha de pago"
              value={actual.recaudado}
            />
            <MontoResumen
              label="Brecha del período"
              value={actual.brecha}
              secondary="No representa deuda pendiente por factura."
            />
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Desglose por sucursal
          </Typography>

          <Stack
            spacing={1.25}
            sx={{ maxHeight: 210, overflowY: "auto", pr: 1 }}
          >
            {porSucursal.map((item) => (
              <Box key={item.sucursal}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Typography variant="body2" noWrap>
                    {item.sucursal}
                  </Typography>
                  <Typography variant="subtitle2">
                    {formatearPorcentaje(item.actual.eficienciaPorcentaje)}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatAsCLP(item.actual.recaudado)} de{" "}
                  {formatAsCLP(item.actual.facturado)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
}
